import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Form } from '@/components/ui/form'
import { Skeleton } from '@/components/ui/skeleton'
import { FormEntityContext } from '../../../../config/context/forms'
import { useGuild } from '../../../../config/context/guild'
import { hasAdminAccessToAllTeams } from '../../../../config/permissions'
import { useGuildLinkCandidates } from '../../../../config/queries'
import { updateLinkedGuilds } from '../../../../api/guild'
import { displayErrorToast } from '../../../../helpers'
import LinkedGuildsTable, {
  linkedGuildIdsFromRows,
  linkedGuildRowsFor,
  linkedGuildRowsSchema,
} from '../../../GuildDashboard/subpages/GuildSettings/components/LinkedGuildsTable'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import { StepProps } from '../types'

const formSchema = z.object({
  linkedGuildRows: linkedGuildRowsSchema,
})

type LinkedGuildsFormValues = z.infer<typeof formSchema>

const LinkedGuildsStep = ({ goNext, goBack, isFirst }: StepProps) => {
  const { t } = useTranslation()
  const { guild } = useGuild()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const { data: candidates = [], isLoading } = useGuildLinkCandidates(
    guild?.id,
    hasAdminAccessToAllTeams(guild),
  )

  const form = useForm<LinkedGuildsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      linkedGuildRows: guild ? linkedGuildRowsFor(guild) : [],
    },
  })

  if (!guild) return null

  const hasLinkableGuilds =
    candidates.length > 0 || guild.linkedGuilds.length > 0

  const onSubmit = (values: LinkedGuildsFormValues) => {
    if (!form.formState.isDirty) {
      goNext()
      return
    }

    setSubmitting(true)

    updateLinkedGuilds(guild.id, linkedGuildIdsFromRows(values.linkedGuildRows))
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['guild', { path: guild.path }],
        })
        queryClient.invalidateQueries({ queryKey: ['guildLinkCandidates'] })
        setSubmitting(false)
        goNext()
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  return (
    <StepLayout
      icon={Link2}
      title='Link your other guilds'
      description={
        <>
          You can link your new guild to guilds in other game versions, if your
          group has presence there as well. This will add direct links in the
          team switcher, to switch between guilds easily.
        </>
      }
    >
      <Form {...form}>
        <FormEntityContext.Provider
          value={{ entityType: 'guild', i18nPrefix: 'forms.fields' }}
        >
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {isLoading ? (
              <Skeleton className='h-32' />
            ) : hasLinkableGuilds ? (
              <>
                <p className='mb-4'>
                  You can only link guilds where you have admin access to all
                  teams.
                </p>
                <LinkedGuildsTable />
              </>
            ) : (
              <Alert variant='info'>
                <AlertTitle>No other guilds found</AlertTitle>
                <AlertDescription>
                  We couldn&apos;t find any other guilds where you have admin
                  access to all teams. Once you do, you can link them in the
                  guild settings.
                </AlertDescription>
              </Alert>
            )}

            <StepFooter
              showBack={!isFirst}
              onBack={goBack}
              onContinue={form.handleSubmit(onSubmit)}
              continueLoading={submitting}
              onSkip={goNext}
            />
          </form>
        </FormEntityContext.Provider>
      </Form>
    </StepLayout>
  )
}

export default LinkedGuildsStep
